import React, { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useApiQuery } from '../../utils/api'
import { Error, Loading } from './Indicators'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend,
  ChartData,
  Point,
  TooltipItem
} from 'chart.js'
import { Chart } from 'react-chartjs-2'
import { XUser } from '@jirax/common'
import {
  ChartBarIcon,
  UsersIcon,
  BugAntIcon,
  StarIcon
} from '@heroicons/react/24/outline'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend
)

// Define the shape of the data returned by the API
interface SprintWithSummaryStats {
  id: number
  name: string
  state: 'active' | 'closed' | 'future'
  stats: {
    startPoints: number
    resolvedPoints: number
    startBugs: number
    resolvedBugs: number
  } | null
}

// Define the shape of the processed data for the report
interface SprintReportData {
  sprintNames: string[]
  committedPoints: number[]
  resolvedPoints: number[]
  velocity: number[]
  totalSprints: number
  totalCommittedPoints: number
  totalResolvedPoints: number
  totalResolvedBugs: number
  avgDeliveryRate: number
}

/**
 * Processes raw sprint data from the API into a format suitable for charts and summary stats.
 * @param sprints - The array of sprints with summary stats from the API.
 * @returns A structured report object.
 */
const processSprintReportData = (
  sprints: SprintWithSummaryStats[]
): SprintReportData => {
  // Filter out any sprints that don't have stats (e.g., active or future sprints)
  const sprintsWithStats = sprints.filter(sprint => sprint.stats)

  // Reverse the order before mapping so we render chronologically (oldest to newest)
  const chronologicalSprints = [...sprintsWithStats].reverse()

  const report = chronologicalSprints.reduce(
    (acc, sprint) => {
      if (sprint.stats) {
        const deliveryRate =
          sprint.stats.startPoints > 0
            ? sprint.stats.resolvedPoints / sprint.stats.startPoints
            : 0

        acc.sprintNames.push(sprint.name)
        acc.committedPoints.push(sprint.stats.startPoints)
        acc.resolvedPoints.push(sprint.stats.resolvedPoints)
        acc.velocity.push(deliveryRate)
        acc.totalCommittedPoints += sprint.stats.startPoints
        acc.totalResolvedPoints += sprint.stats.resolvedPoints
        acc.totalResolvedBugs += sprint.stats.resolvedBugs
      }
      return acc
    },
    {
      sprintNames: [] as string[],
      committedPoints: [] as number[],
      resolvedPoints: [] as number[],
      velocity: [] as number[],
      totalCommittedPoints: 0,
      totalResolvedPoints: 0,
      totalResolvedBugs: 0
    }
  )

  const totalSprints = chronologicalSprints.length
  const avgDeliveryRate =
    report.totalCommittedPoints > 0
      ? report.totalResolvedPoints / report.totalCommittedPoints
      : 0

  return {
    ...report,
    totalSprints,
    avgDeliveryRate
  }
}

/**
 * A reusable component to display a single statistic with an icon.
 */
const StatCard: React.FC<{
  icon: React.ElementType
  title: string
  value: string | number
  subValue?: string | number
  subTitle?: string
  iconBgColor?: string
  tooltip?: string
}> = ({
  icon: Icon,
  title,
  value,
  subValue,
  subTitle,
  iconBgColor = 'bg-sky-500',
  tooltip
}) => (
    <div
      className="relative overflow-hidden rounded-lg bg-white p-5 shadow"
      {...(tooltip ? { title: tooltip } : {})}
    >
      <div className="flex items-center">
        <div
          className={`absolute -ml-2 rounded-md p-3 ${iconBgColor}`}
        >
          <Icon className="h-6 w-6 text-white" aria-hidden="true" />
        </div>
        <div className="ml-16 w-full">
          <p className="truncate text-sm font-medium text-gray-500">{title}</p>
          <div className="flex items-baseline">
            <p className="text-2xl font-semibold text-gray-900">{value}</p>
          </div>
        </div>
        {subValue && subTitle && (
          <div className="ml-4 w-full border-l border-gray-200 pl-4">
            <p className="truncate text-sm font-medium text-gray-500">
              {subTitle}
            </p>
            <div className="flex items-baseline">
              <p className="text-2xl font-semibold text-gray-900">{subValue}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )

/**
 * Displays a grid of summary statistics for the team's performance.
 */
const SummaryStats: React.FC<{
  report: SprintReportData
  numUsers: number
}> = ({ report, numUsers }) => {
  const {
    totalSprints,
    avgDeliveryRate,
    totalResolvedBugs,
    totalResolvedPoints
  } = report

  // --- Calculate Averages ---
  // Avoid division by zero if there are no sprints or users
  const safeNumSprints = totalSprints > 0 ? totalSprints : 1
  const safeNumUsers = numUsers > 0 ? numUsers : 1

  const avgBugsPerSprint = (totalResolvedBugs / safeNumSprints).toFixed(1)
  const avgPointsPerSprint = (totalResolvedPoints / safeNumSprints).toFixed(1)

  const avgBugsPerMember = (
    totalResolvedBugs /
    (safeNumSprints * safeNumUsers)
  ).toFixed(1)
  const avgPointsPerMember = (
    totalResolvedPoints /
    (safeNumSprints * safeNumUsers)
  ).toFixed(1)

  /* --- Tooltip helper text ------------------------------------------- */
  const tt = {
    bugsTeam: 'Average bugs resolved by the team per sprint',
    bugsMember: 'Average bugs resolved per team member per sprint',
    ptsTeam: 'Average story points (SP) resolved by the team per sprint',
    ptsMember: 'Average story points (SP) resolved per team member per sprint',
    delivery: 'Average % of committed story points delivered per sprint'
  }

  return (
    <div>
      <h3 className="text-base font-semibold leading-6 text-gray-900">
        Team Performance
      </h3>
      <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {/* --- Team & Performance Stats --- */}
        <StatCard
          icon={UsersIcon}
          title="Members"
          value={numUsers}
          subTitle="Sprints"
          subValue={totalSprints}
          iconBgColor="bg-fuchsia-500"
        />
        <StatCard
          icon={ChartBarIcon}
          title="Del. Rate"
          value={`${(avgDeliveryRate * 100).toFixed(0)}%`}
          tooltip={tt.delivery}
          iconBgColor="bg-cyan-500"
        />
        {/* --- Bug Stats --- */}
        <StatCard
          icon={BugAntIcon}
          title="Team"
          value={avgBugsPerSprint}
          subTitle="Per Member"
          subValue={avgBugsPerMember}
          tooltip={tt.bugsTeam}
          iconBgColor="bg-rose-500"
        />
        {/* --- Point Stats --- */}
        <StatCard
          icon={StarIcon}
          title="Team"
          value={avgPointsPerSprint}
          subTitle="Per Member"
          subValue={avgPointsPerMember}
          tooltip={tt.ptsTeam}
          iconBgColor="bg-amber-500"
        />
      </div>
    </div>
  )
}

/**
 * Renders the main bar/line chart for sprint commitment vs. delivery.
 */
const SprintChart: React.FC<{ report: SprintReportData }> = ({ report }) => {
  const chartData = useMemo<ChartData<'bar' | 'line'>>(() => {
    return {
      labels: report.sprintNames,
      datasets: [
        {
          type: 'bar' as const,
          label: 'Committed Points',
          data: report.committedPoints,
          backgroundColor: 'rgba(54, 162, 235, 0.5)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1,
          yAxisID: 'y'
        },
        {
          type: 'bar' as const,
          label: 'Resolved Points',
          data: report.resolvedPoints,
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
          yAxisID: 'y'
        },
        {
          type: 'line' as const,
          label: 'Velocity',
          data: report.velocity.map(v => v * 100), // Convert to percentage
          borderColor: 'rgba(255, 99, 132, 1)',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          yAxisID: 'y1',
          tension: 0.4
        }
      ]
    }
  }, [report])

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const
      },
      title: {
        display: true,
        text: 'Sprint Commitment & Velocity',
        font: {
          size: 18
        }
      },
      tooltip: {
        callbacks: {
          label: function (context: TooltipItem<'bar' | 'line'>) {
            let label = context.dataset.label || ''
            if (label) {
              label += ': '
            }
            if (context.dataset.yAxisID === 'y1') {
              label += `${context.formattedValue}%` // Add percentage for velocity
            } else {
              label += context.formattedValue
            }
            return label
          }
        }
      }
    },
    scales: {
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        title: {
          display: true,
          text: 'Story Points'
        }
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        grid: {
          drawOnChartArea: false // only show the grid for the primary axis
        },
        ticks: {
          callback: function (value: string | number) {
            return value + '%' // Add percentage sign to the velocity axis
          }
        },
        title: {
          display: true,
          text: 'Velocity (%)'
        }
      }
    }
  }

  return (
    <div className="mt-8">
      <Chart
        type="bar"
        data={chartData as ChartData<'bar', (number | [number, number] | Point | null)[], unknown>}
        options={chartOptions}
      />
    </div>
  )
}

/**
 * Main component to display team analysis, including summary stats and charts.
 */
export const TeamAnalysis: React.FC = () => {
  const { boardId } = useParams<{ boardId: string }>()

  // Fetch sprint summary data
  const {
    data: sprints,
    isLoading: sprintsLoading,
    error: sprintsError
  } = useApiQuery<SprintWithSummaryStats[]>(
    ['boards', boardId, 'stats', 'summary'],
    { enabled: !!boardId }
  )

  // Fetch board users to get team size
  const {
    data: users,
    isLoading: usersLoading,
    error: usersError
  } = useApiQuery<XUser[]>(['boards', boardId, 'users'], { enabled: !!boardId })

  // Memoize processed data to avoid re-calculation on every render
  const sprintReport = useMemo(
    () => (sprints ? processSprintReportData(sprints) : null),
    [sprints]
  )

  if (sprintsLoading || usersLoading) {
    return <Loading message={'Loading team analysis...'} />
  }

  if (sprintsError || usersError) {
    const error = sprintsError || usersError
    return <Error message={(error as Error).message} />
  }

  if (!sprintReport || sprintReport.totalSprints === 0) {
    return (
      <div className="text-center text-gray-500">
        No Sprint Data Available
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <SummaryStats report={sprintReport} numUsers={users?.length ?? 0} />
      <SprintChart report={sprintReport} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Placeholder export so that other pages importing `UserAnalysis` continue to
// compile until the real implementation is added/refactored.
// ---------------------------------------------------------------------------
//  INDIVIDUAL  USER  ANALYSIS
// ---------------------------------------------------------------------------

interface UserForAnalysis {
  id: string
  name: string
  avatar?: string
}

interface UserAnalysisProps {
  boardId: string
  /** The Jira project key the user’s sprints belong to.
   *  Optional so <UserAnalysis> can still be rendered in contexts
   *  (e.g. user-centric pages) where the project key is not readily
   *  available.  When omitted, callers must ensure the underlying
   *  query key they pass to `useApiQuery` includes a stable placeholder
   *  (or accept that no data will be fetched). */
  projectKey?: string
  user: UserForAnalysis
}

export const UserAnalysis: React.FC<UserAnalysisProps> = ({
  boardId,
  user
}) => {
  /* -------------------------------- API data ----------------------------- */
  const {
    data: sprints,
    isLoading,
    error
  } = useApiQuery<SprintWithSummaryStats[]>(
    ['boards', boardId, 'stats', 'user', user.id],
    { enabled: Boolean(user.id && boardId) }
  )

  /* ------------------------ Derive user-specific report ------------------ */
  const sprintReport = useMemo(
    () => (sprints ? processSprintReportData(sprints) : null),
    [sprints]
  )

  /* ---------------------------  Loading / Error  ------------------------- */
  if (isLoading) {
    return <Loading message={'Loading user analysis…'} />
  }

  if (error) {
    return <Error message={(error as Error).message} />
  }

  if (!sprintReport || sprintReport.totalSprints === 0) {
    return (
      <div className="text-center text-gray-500">
        No Sprint Data Available
      </div>
    )
  }

  /* --------------------------  User Summary Stats  ----------------------- */
  const {
    totalSprints,
    avgDeliveryRate,
    totalResolvedBugs,
    totalResolvedPoints
  } = sprintReport

  const avgBugsPerSprint = (
    totalResolvedBugs / (totalSprints || 1)
  ).toFixed(1)
  const avgPointsPerSprint = (
    totalResolvedPoints / (totalSprints || 1)
  ).toFixed(1)

  const tt = {
    delivery:
      'Average % of story points delivered by this user across analysed sprints',
    bugs: 'Average bugs resolved by this user per sprint',
    pts: 'Average story points (SP) resolved by this user per sprint'
  }

  const UserSummary: React.FC = () => (
    <div>
      <h3 className="text-base font-semibold leading-6 text-gray-900">
        {user.name}
      </h3>
      <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={ChartBarIcon}
          title="Sprints"
          value={totalSprints}
          iconBgColor="bg-fuchsia-500"
        />
        <StatCard
          icon={ChartBarIcon}
          title="Del. Rate"
          value={`${(avgDeliveryRate * 100).toFixed(0)}%`}
          tooltip={tt.delivery}
          iconBgColor="bg-cyan-500"
        />
        <StatCard
          icon={BugAntIcon}
          title="Bugs"
          value={avgBugsPerSprint}
          tooltip={tt.bugs}
          iconBgColor="bg-rose-500"
        />
        <StatCard
          icon={StarIcon}
          title="Points"
          value={avgPointsPerSprint}
          tooltip={tt.pts}
          iconBgColor="bg-amber-500"
        />
      </div>
    </div>
  )

  /* ---------------------------  Render layout  --------------------------- */
  return (
    <div className="space-y-8">
      <UserSummary />
      <SprintChart report={sprintReport} />
    </div>
  )
}